import { Alert } from '@pmi/dsm-react';
import { BlockIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { Spinner } from 'react-bootstrap';
import jwtDecode from 'jwt-decode';
import { JwtClaims } from 'oidc-client-ts';
import { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  getOidcAuthority,
  getOidcClientId,
  getOidcToken,
} from '../../../shared/env-vars';
import { verifyUserFlow } from '../../../shared/helpers';
import useUrlSearchParams from '../../../shared/hooks/useUrlSearchParams';
import {
  useResolveClientTokenMutation,
  useValidateTokenMutation,
} from '../../../shared/navigation/TwoFactorAuthCallback/apiSlice';
import AuthService from '../../../shared/services/AuthService';
import { setUser } from '../../../shared/store/shared/userSlice';
import { setHubPageTitle } from '../../hubLayoutSlice';
import { setHasImpersonatedBy } from '../../../shared/store/shared/impersonationBySlice';

interface ProtectedRouteProps {
  pageName: string;
  render: () => ReactElement;
}

export default function ProtectedHubRoute({
  render,
  pageName,
}: ProtectedRouteProps): ReactElement {
  const { accountId } = useParams<{ accountId: string }>();
  const urlSearchParams = useUrlSearchParams();
  const [errorMessage, setErrorMessage] = useState('');
  const [resolveClientToken] = useResolveClientTokenMutation();
  const [validateToken] = useValidateTokenMutation();
  const navigate = useNavigate();
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const token = urlSearchParams.get('token')?.toString();
  const clientTokenId = urlSearchParams.get('code')?.toString();
  const authService = new AuthService();
  const dispatch = useDispatch();
  const location = useLocation();

  const removeCodeParameterFromUrl = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('code');
    window.history.replaceState({}, '', currentUrl.toString());
  };

  useEffect(() => {
    dispatch(setHubPageTitle(pageName));
  }, [pageName]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const processIncomingToken = async () => {
      let resolvedToken = token;

      if (clientTokenId) {
        try {
          const tokenResponse = await resolveClientToken({
            clientTokenId,
            routeType: 'b2b',
          }).unwrap();
          resolvedToken = tokenResponse?.token;
        } catch {
          setErrorMessage('The token is invalid');
          setIsLoadingAuth(false);
          return;
        } finally {
          removeCodeParameterFromUrl();
        }
      }

      if (resolvedToken) {
        try {
          const jwt = jwtDecode<JwtClaims>(resolvedToken, { header: false });

          // check if jwt has impersonated_by property and dispatch
          if (jwt?.impersonated_by) {
            dispatch(setHasImpersonatedBy(true));
          }

          if (
            jwt?.client_id !== getOidcClientId() ||
            !jwt.b2b_aid ||
            (jwt?.b2b_aid as string).toUpperCase() !== accountId.toUpperCase()
          ) {
            setErrorMessage('The token has invalid credentials');
            setIsLoadingAuth(false);
          } else {
            validateToken(resolvedToken)
              .unwrap()
              .then(async () => {
                const storedToken = JSON.parse(
                  localStorage.getItem(getOidcToken()),
                );
                const newToken = {
                  ...storedToken,
                  access_token: resolvedToken,
                  expires_at: jwt.exp,
                };
                localStorage.setItem(getOidcToken(), JSON.stringify(newToken));
                if (
                  window.opener &&
                  window.opener !== window &&
                  !window.menubar.visible
                ) {
                  await authService.loginPopupCallback();
                  return;
                }

                window.location.href = window.location.pathname;
              })
              .catch(() => {
                setErrorMessage(
                  'Unauthorized! Please make sure to verify your Account info or select a different Account',
                );
                setIsLoadingAuth(false);
              });
            // @ts-ignore
            return;
          }
        } catch (error) {
          setErrorMessage('The token is invalid');
          setIsLoadingAuth(false);
          // @ts-ignore
          return;
        }
      } else {
        const userSession = localStorage.getItem(getOidcToken());
        if (userSession) {
          const parsedToken = JSON.parse(userSession);
          const storedJwt = jwtDecode<JwtClaims>(parsedToken?.access_token);

          // check if storedJwt has impersonate_by property and dispatch
          if (storedJwt?.impersonated_by) {
            dispatch(setHasImpersonatedBy(true));
          }

          if (
            !getOidcAuthority().includes(storedJwt?.iss) ||
            !storedJwt?.b2b_aid ||
            (storedJwt?.b2b_aid as string).toUpperCase() !==
              accountId.toUpperCase()
          ) {
            verifyUserFlow(accountId);
            // @ts-ignore
            return;
          }
          authService.getUser().then((res) => {
            if (res) {
              dispatch(setUser({ ...res }));
            } else {
              authService.logout();
            }
          });
        } else {
          authService.login(location.pathname + location.search);
          // @ts-ignore
          return;
        }
      }

      timeout = setTimeout(() => {
        setIsLoadingAuth(false);
      }, 500);
    };

    processIncomingToken();

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [token, clientTokenId, accountId]);

  if (!isLoadingAuth && errorMessage)
    return (
      <Alert
        show
        variant="danger"
        onClose={() => navigate('/')}
        alertText={errorMessage}
        onActionableLinkClick={() => navigate('/hub')}
        actionableLinkText="Select A Different Account"
        dismissButtonLabel="Close"
        onDismissButtonClick={() => navigate('/')}
        icon={BlockIcon}
      />
    );

  if (isLoadingAuth) return <Spinner />;

  return <>{render()}</>;
}
